import { useDeferredValue, useEffect, useState } from 'react';
import { PartialIntegrationFieldDto } from './integrationTypes/integrationTypes';
import { differenceInMilliseconds, intervalToDuration } from 'date-fns';

export const getCurrentTimeAtSite = (
  timeZone?: string,
  opts?: {
    includeCalendar?: boolean;
    includeTimezone?: boolean;
    includeTime?: boolean;
  }
) => {
  console.log(timeZone);
  const { includeCalendar = true, includeTimezone = true, includeTime = true } = opts ?? {};

  const parts: string[] = [];

  const _timeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (includeCalendar) {
    const calendarString = new Date().toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      timeZone: _timeZone,
    });
    parts.push(calendarString + ',');
  }

  if (includeTime) {
    const timeStr = new Date().toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: _timeZone,
    });

    parts.push(timeStr);
  }

  if (includeTimezone) {
    const timeZoneName = new Intl.DateTimeFormat('en-NZ', {
      timeZone: _timeZone,
      timeZoneName: 'short',
    })
      .formatToParts()
      .find((part) => part.type === 'timeZoneName')?.value;

    if (timeZoneName) {
      parts.push(timeZoneName);
    }
  }

  return parts.join(' ').replace(/,$/, '');
};

export const getTimeZoneName = (timeZone?: string) => {
  const shortName = new Intl.DateTimeFormat('en-NZ', {
    timeZoneName: 'short',
    hour12: true,
    timeZone,
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName')?.value;

  const gmtOffset = new Intl.DateTimeFormat('en-NZ', {
    timeZoneName: 'shortOffset',
    timeZone,
  })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName')?.value;

  if (shortName && gmtOffset) {
    return `${shortName} (${gmtOffset})`;
  }
  return shortName || gmtOffset;
};

/** @deprecated use usePeriod instead */
export const useCurrentTime = (updateIntervalMs = 10 * 1000) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timeToNextInterval = updateIntervalMs - (Date.now() % updateIntervalMs);

    const interval = setInterval(() => {
      setTime(new Date());
    }, updateIntervalMs);

    return () => clearInterval(interval);
  }, [updateIntervalMs, setTime]);

  return time;
};

/** Runs the given function each interval, snapped to 0-time */
export const usePeriod = <T = any>(fn: () => T, updateIntervalMs = 10 * 1000) => {
  const [value, setValue] = useState<T>(fn());
  const deferredValue = useDeferredValue(value);

  useEffect(() => {
    const timeToNextInterval = updateIntervalMs - (Date.now() % updateIntervalMs);

    let interval: NodeJS.Timeout;

    // Timeout to initial period and then run on interval
    const timeout = setTimeout(() => {
      setValue(fn());
      interval = setInterval(() => {
        setValue(fn());
      }, updateIntervalMs);
    }, timeToNextInterval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      clearTimeout(timeout);
    };
  }, [updateIntervalMs, setValue]);

  return deferredValue;
};

type DurationFormat = {
  days: [string, string];
  hours: [string, string];
  minutes: [string, string];
  seconds: [string, string];
  sep: string;
  numeric: boolean;
};

const FormatStyle = {
  numeric: {
    days: ['', ''],
    hours: ['', ''],
    minutes: ['', ''],
    seconds: ['', ''],
    sep: ':',
    numeric: true,
  },
  default: {
    days: [' day', ' days'],
    hours: [' hour', ' hours'],
    minutes: [' minute', ' minutes'],
    seconds: [' second', ' seconds'],
    sep: ' ',
    numeric: false,
  },
  short: {
    days: ['d ', 'd '],
    hours: ['h ', 'h '],
    minutes: ['m ', 'm '],
    seconds: ['s ', 's '],
    sep: ' ',
    numeric: false,
  },
} satisfies Record<string, DurationFormat>;

export type FormatDurationFormat = Partial<DurationFormat> | keyof typeof FormatStyle;

export const formatDuration = (ms: number, formats?: FormatDurationFormat) => {
  const _format = typeof formats === 'string' ? FormatStyle[formats] : formats;

  const f = {
    ...FormatStyle.default,
    ..._format,
  };

  const int = intervalToDuration({ start: 0, end: ms });

  let parts: [number, [string, string]][] = [
    [int.days ?? 0, f.days],
    [int.hours ?? 0, f.hours],
    [int.minutes ?? 0, f.minutes],
    [int.seconds ?? 0, f.seconds],
  ];

  const firstIdx = parts.findIndex((x) => x[0] > 0);
  const startIdx = f.numeric ? Math.min(2, firstIdx) : firstIdx;

  const filtered = parts.slice(startIdx);

  if (f.numeric) {
    return filtered
      .map(
        ([value = 0, format], i, arr) =>
          `${i > 0 || i === arr.length - 1 ? String(value).padStart(2, '0') : value}${format[Number(value !== 1)]}`
      )
      .join(f.sep);
  }

  return filtered.map(([value, format]) => `${value}${format[Number(value !== 1)]}`).join(f.sep);
};

/** Set a timeout for an absolute date */
export const setTimeoutAt = (fn: () => void, date: Date) => {
  const timeFromNow = differenceInMilliseconds(date, new Date());

  if (timeFromNow < 1) return;

  return setTimeout(() => {
    fn();
  }, timeFromNow);
};
